import React from 'react';
import {Box, Chip, Typography} from '@mui/material';
import {ActeBlock, BulletLine, Eyebrow, PhotoTile} from '../primitives.js';
import {BLOC, NATURE_TOKENS, PHOTO_LABEL_COLORS, PROBLEME_TOKENS} from '../interventionRules.js';

const AvantApres = ({photosAvant, photosApres, assetsUrl, onOpen}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
        <PhotoTile photos={photosAvant} label="Avant" color={PHOTO_LABEL_COLORS.avant} assetsUrl={assetsUrl} onOpen={onOpen}/>
        <PhotoTile photos={photosApres} label="Après" color={PHOTO_LABEL_COLORS.apres} assetsUrl={assetsUrl} onOpen={onOpen}/>
    </Box>
);

/** Bloc « Lavage » : avant / après côte à côte. */
export const LavageBlock = ({numero, bloc, assetsUrl, onOpen}) => (
    <ActeBlock numero={numero} titre={bloc.titre} valeur={bloc.valeur} tokens={NATURE_TOKENS[BLOC.LAVAGE]}>
        <AvantApres photosAvant={bloc.photosAvant} photosApres={bloc.photosApres} assetsUrl={assetsUrl} onOpen={onOpen}/>
    </ActeBlock>
);

/** Encart raison(s) + photo inaccessible, réutilisé par « impossible » et « partiel » (photo à la taille avant / après). */
const RaisonEncart = ({eyebrow, raisons, photos, assetsUrl, onOpen, framed = false}) => (
    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2,
        ...(framed ? {p: 2, borderRadius: '12px', bgcolor: PROBLEME_TOKENS.bg, border: `1px solid ${PROBLEME_TOKENS.border}`} : {})}}>
        <Box>
            <Eyebrow sx={{mb: 1}}>{eyebrow}</Eyebrow>
            {raisons.length > 0
                ? raisons.map((r, i) => <BulletLine key={i} color={PROBLEME_TOKENS.ink}>{r}</BulletLine>)
                : <Typography variant="body2" sx={{color: '#8A929C', fontStyle: 'italic'}}>Raison non renseignée</Typography>}
        </Box>
        <PhotoTile photos={photos} label="Lavage impossible" color={PHOTO_LABEL_COLORS.inaccessible} assetsUrl={assetsUrl} onOpen={onOpen}/>
    </Box>
);

/** Bloc « Lavage impossible » : raison(s) + photo inaccessible. */
export const LavageImpossibleBlock = ({numero, bloc, assetsUrl, onOpen}) => (
    <ActeBlock numero={numero} titre={bloc.titre} valeur={bloc.valeur} tokens={NATURE_TOKENS[BLOC.LAVAGE_IMPOSSIBLE]}>
        <RaisonEncart eyebrow="Raison" raisons={bloc.raisons} photos={bloc.photosInaccessible} assetsUrl={assetsUrl} onOpen={onOpen}/>
    </ActeBlock>
);

/** Bloc « Lavage partiel » : un lavage dégradé — encart « raison » (cause de la dégradation) puis avant / après. */
export const LavagePartielBlock = ({numero, bloc, assetsUrl, onOpen}) => (
    <ActeBlock numero={numero} titre={bloc.titre} valeur={bloc.valeur} tokens={NATURE_TOKENS[BLOC.LAVAGE_PARTIEL]}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 2.5}}>
            <RaisonEncart eyebrow="Raison du lavage partiel" raisons={bloc.raisons} photos={bloc.photosInaccessible} assetsUrl={assetsUrl} onOpen={onOpen} framed/>
            <Box>
                <Eyebrow sx={{mb: 1.5}}>Lavage</Eyebrow>
                <AvantApres photosAvant={bloc.photosAvant} photosApres={bloc.photosApres} assetsUrl={assetsUrl} onOpen={onOpen}/>
            </Box>
        </Box>
    </ActeBlock>
);

/** Bloc v1 « Maintenance signalée » : problèmes libres + photo problem. */
export const MaintenanceSignaleeBlock = ({numero, bloc, assetsUrl, onOpen}) => {
    const tokens = NATURE_TOKENS[BLOC.MAINTENANCE_SIGNALEE];
    return (
        <ActeBlock numero={numero} titre={bloc.titre} valeur={bloc.valeur} tokens={tokens}
                   chip={<Chip size="small" label="Signalement libre" sx={{bgcolor: '#fff', color: tokens.ink, fontWeight: 600, height: 22}}/>}>
            <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
                <Box>
                    <Eyebrow sx={{mb: 1}}>Problèmes constatés</Eyebrow>
                    {bloc.raisons.map((r, i) => <BulletLine key={i} color={tokens.dot}>{r}</BulletLine>)}
                </Box>
                <PhotoTile photos={bloc.photosProblem} label="Problème" color={PHOTO_LABEL_COLORS.problem} assetsUrl={assetsUrl} onOpen={onOpen}/>
            </Box>
        </ActeBlock>
    );
};
